import { Extension } from "@tiptap/core";
import { Node as ProseMirrorNode } from "@tiptap/pm/model";
import { Selection, Transaction } from "@tiptap/pm/state";
import { addColumn, addRow, TableMap } from "@tiptap/pm/tables";
import { getTableContext, reorderTableColumns, reorderTableRows, TableContext } from "./MentionInputTable.utils.ts";

declare module "@tiptap/core" {
    interface Commands<ReturnType> {
        tableCommands: {
            moveTableRow: (from: number, to: number) => ReturnType;
            moveTableColumn: (from: number, to: number) => ReturnType;
            addRowAtEnd: () => ReturnType;
            addColumnAtEnd: () => ReturnType;
        };
    }
}

// @tiptap/extension-table has no move command, and its add-row/column commands insert next to the
// caret's cell. These fill both gaps so the table controls can work off row/column indices instead.
export const TableCommands = Extension.create({
    name: "tableCommands",

    addCommands() {
        return {
            moveTableRow:
                (from, to) =>
                ({ state, tr, dispatch }) => {
                    const context = getTableContext(state.selection);
                    if (!context) {
                        return false;
                    }
                    const reordered = reorderTableRows(context.table, from, to);
                    if (!reordered) {
                        return false;
                    }
                    if (dispatch) {
                        replaceTable(tr, context, reordered);
                        selectCell(tr, context.tableStart, reordered, to, context.columnIndex);
                    }
                    return true;
                },

            moveTableColumn:
                (from, to) =>
                ({ state, tr, dispatch }) => {
                    const context = getTableContext(state.selection);
                    if (!context) {
                        return false;
                    }
                    const reordered = reorderTableColumns(context.table, from, to);
                    if (!reordered) {
                        return false;
                    }
                    if (dispatch) {
                        replaceTable(tr, context, reordered);
                        selectCell(tr, context.tableStart, reordered, context.rowIndex, to);
                    }
                    return true;
                },

            addRowAtEnd:
                () =>
                ({ state, tr, dispatch }) => {
                    const context = getTableContext(state.selection);
                    if (!context) {
                        return false;
                    }
                    if (dispatch) {
                        addRow(tr, toTableRect(context), context.map.height);
                    }
                    return true;
                },

            addColumnAtEnd:
                () =>
                ({ state, tr, dispatch }) => {
                    const context = getTableContext(state.selection);
                    if (!context) {
                        return false;
                    }
                    if (dispatch) {
                        addColumn(tr, toTableRect(context), context.map.width);
                    }
                    return true;
                },
        };
    },
});

function toTableRect({ map, tableStart, table }: TableContext) {
    return { map, tableStart, table, left: 0, top: 0, right: map.width, bottom: map.height };
}

function replaceTable(tr: Transaction, context: TableContext, reordered: ProseMirrorNode) {
    tr.replaceWith(context.tablePos, context.tablePos + context.table.nodeSize, reordered);
}

function selectCell(tr: Transaction, tableStart: number, table: ProseMirrorNode, rowIndex: number, columnIndex: number) {
    const cellOffset = TableMap.get(table).positionAt(rowIndex, columnIndex, table);
    tr.setSelection(Selection.near(tr.doc.resolve(tableStart + cellOffset + 1)));
}
